import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Scrollbar } from '@/components/atoms/scrollbar';
import { Status } from '@/components/atoms/status';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { DiscountHistoryHost } from '@/features/hosts/api/types/discount';

interface Props {
  data: DiscountHistoryHost[];
  error: string;
  isLoading: boolean;
}

export const HostsDiscountHistory = ({ data, error, isLoading }: Props) => {
  const { t } = useTranslation(['host']);

  const columns = [
    {
      id: 'ticketNumber',
      label: t('host:discount.ticketNumber'),
    },
    {
      id: 'ticketTransactionNumber',
      label: t('host:discount.ticketTransactionNumber'),
    },
    {
      id: 'status',
      label: t('host:discount.status'),
    },
    {
      id: 'paidAt',
      label: t('host:discount.paidAt'),
    },
  ];

  const handleColorStatus = (status: string) => {
    switch (status) {
      case 'assigned':
        return '#1b774d';
      case 'discounted':
        return '#736Be9';
      case 'discount_error':
        return '#ef6f6b';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(discount => (
              <TableRow hover key={discount.id}>
                <TableCell>
                  <Chip variant="outlined" color="success" label={discount.ticketNumber} />
                </TableCell>
                <TableCell>
                  <Chip variant="outlined" color="info" label={discount.ticketTransactionNumber} />
                </TableCell>
                <TableCell>
                  <Status
                    color={handleColorStatus(discount?.status) || ''}
                    label={discount?.status}
                  />
                </TableCell>
                <TableCell>
                  {discount.paidAt ? (
                    <>
                      <Typography color="inherit" variant="inherit">
                        {format(new Date(discount.paidAt), 'dd MMM yyyy')}
                      </Typography>
                      <Typography color="textSecondary" variant="inherit">
                        {format(new Date(discount.paidAt), 'HH:mm')}
                      </Typography>
                    </>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator dataLength={data?.length} error={error} isFetching={isLoading} />
    </Box>
  );
};
